import { useState, useEffect } from 'react';
import { Clock, CheckCircle, Circle, Eye, FileText, User, ClipboardList, Heart, ScrollText, Download, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { DocumentosRHInterface } from '@/components/DocumentosRHInterface';
import DocumentosValidacaoRH from '@/components/DocumentosValidacaoRH';
import { usePermissions } from '@/hooks/usePermissions';
import ExameAprovacaoModal from '@/components/ExameAprovacaoModal';

// Componente separado para renderizar documentos
interface DocumentViewerProps {
  documento: any;
}

function DocumentViewer({ documento }: DocumentViewerProps) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getSignedUrl = async () => {
      if (!documento?.caminho_storage) {
        setUrlError(true);
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase.storage
          .from('contratacao-documentos')
          .createSignedUrl(documento.caminho_storage, 3600); // 1 hora de validade

        if (error) {
          console.error('Erro ao criar URL assinada:', error);
          setUrlError(true);
        } else {
          setSignedUrl(data.signedUrl);
        }
      } catch (error) {
        console.error('Erro ao criar URL assinada:', error);
        setUrlError(true);
      } finally {
        setLoading(false);
      }
    };

    getSignedUrl();
  }, [documento?.caminho_storage]);

  if (loading) {
    return <div className="text-muted-foreground">Carregando documento...</div>;
  }

  if (urlError || !signedUrl) {
    return <div className="text-muted-foreground">Erro ao carregar documento</div>;
  }

  // Determinar tipo do arquivo pela extensão
  const fileExtension = documento.nome_arquivo?.split('.').pop()?.toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension || '');
  const isPdf = fileExtension === 'pdf';

  if (isImage) {
    return (
      <div className="flex justify-center">
        <img 
          src={signedUrl} 
          alt={documento.tipo_documento}
          className="max-w-full h-auto max-h-96 border rounded"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
            (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
          }}
        />
        <div className="hidden text-muted-foreground">
          Erro ao carregar imagem. 
          <a 
            href={signedUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary underline ml-1"
          >
            Clique aqui para tentar visualizar
          </a>
        </div>
      </div>
    );
  }

  if (isPdf) {
    return (
      <div className="space-y-2">
        <iframe
          src={`${signedUrl}#view=FitH`}
          className="w-full h-96 border rounded"
          title={documento.tipo_documento}
        />
        <div className="text-center space-x-4">
          <a 
            href={signedUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary underline inline-flex items-center gap-1"
          >
            <Eye className="h-4 w-4" />
            Abrir PDF em nova aba
          </a>
          <a 
            href={signedUrl} 
            download={documento.nome_arquivo}
            className="text-primary underline inline-flex items-center gap-1"
          >
            <Download className="h-4 w-4" />
            Baixar PDF
          </a>
        </div>
      </div>
    );
  }

  // Para outros tipos de arquivo
  return (
    <div className="text-center p-4 border rounded bg-muted/30">
      <p className="mb-2">Tipo de arquivo: {fileExtension?.toUpperCase()}</p>
      <a 
        href={signedUrl} 
        download={documento.nome_arquivo}
        className="text-primary underline inline-flex items-center gap-1"
      >
        <Download className="h-4 w-4" />
        Baixar arquivo
      </a>
    </div>
  );
}

// Componente para lista de documentos com visualização sob demanda
interface DocumentListViewProps {
  documentos: any[];
}

function DocumentListView({ documentos }: DocumentListViewProps) {
  const [selectedDocument, setSelectedDocument] = useState<any | null>(null);

  if (documentos.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>Nenhum documento encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!selectedDocument ? (
        // Lista de documentos
        <div className="space-y-3">
          <h4 className="font-medium text-lg mb-4">Documentos Enviados ({documentos.length})</h4>
          
          {documentos.map((doc) => (
            <div 
              key={doc.id} 
              className="border rounded-lg p-4 hover:bg-muted/30 transition-colors cursor-pointer"
              onClick={() => setSelectedDocument(doc)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <h5 className="font-medium">{doc.tipo_documento}</h5>
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">{doc.nome_arquivo}</p>
                  <p className="text-xs text-muted-foreground">
                    Enviado em: {new Date(doc.created_at).toLocaleString('pt-BR')}
                  </p>
                  {doc.observacoes && (
                    <p className="text-sm mt-2 text-muted-foreground">
                      <strong>Observações:</strong> {doc.observacoes}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <Badge variant={doc.status_validacao === 'aprovado' ? 'default' : 'secondary'}>
                    {doc.status_validacao}
                  </Badge>
                  <Button variant="outline" size="sm" className="text-xs">
                    <Eye className="h-3 w-3 mr-1" />
                    Visualizar
                  </Button>
                </div>
              </div>
            </div>
          ))}
          
          <div className="mt-4 p-3 bg-muted/50 rounded-md border border-dashed">
            <p className="text-sm text-muted-foreground text-center">
              💡 <strong>Dica:</strong> Clique em qualquer documento para visualizá-lo
            </p>
          </div>
        </div>
      ) : (
        // Visualização de documento específico
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setSelectedDocument(null)}
              className="flex items-center gap-1"
            >
              ← Voltar à lista
            </Button>
            <div className="flex-1">
              <h4 className="font-medium">{selectedDocument.tipo_documento}</h4>
              <p className="text-sm text-muted-foreground">{selectedDocument.nome_arquivo}</p>
            </div>
            <div className="flex gap-2">
              <Badge variant={selectedDocument.status_validacao === 'aprovado' ? 'default' : 'secondary'}>
                {selectedDocument.status_validacao}
              </Badge>
              <DocumentDownloadButton documento={selectedDocument} />
            </div>
          </div>
          
          {selectedDocument.observacoes && (
            <div className="bg-muted/30 p-3 rounded-md border">
              <p className="text-sm">
                <strong>Observações:</strong> {selectedDocument.observacoes}
              </p>
            </div>
          )}
          
          <div className="border rounded-lg p-4 bg-white">
            <DocumentViewer documento={selectedDocument} />
          </div>
        </div>
      )}
    </div>
  );
}

interface HistoricoEtapa {
  etapa: string;
  status: string;
  dataHora: string;
}

interface ContratacaoHistoricoProps {
  contratacaoId: string;
  userContratado?: string; // ID do usuário contratado se disponível
}

interface ViewData {
  proposta?: any;
  dadosPessoais?: any;
  documentos?: any[];
  atestadoSaude?: any;
  exameInfo?: any;
  contratos?: any[];
}

// Componente para botão de download
interface DocumentDownloadButtonProps {
  documento: any;
}

function DocumentDownloadButton({ documento }: DocumentDownloadButtonProps) {
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const handleDownload = async () => {
    if (!documento?.caminho_storage) return;

    try {
      const { data, error } = await supabase.storage
        .from('contratacao-documentos')
        .createSignedUrl(documento.caminho_storage, 3600);

      if (error) {
        console.error('Erro ao criar URL de download:', error);
        return;
      }

      setDownloadUrl(data.signedUrl);
      
      // Abrir o download
      const link = document.createElement('a');
      link.href = data.signedUrl;
      link.download = documento.nome_arquivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Erro ao baixar documento:', error);
    }
  };

  return (
    <Button 
      variant="outline" 
      size="sm"
      onClick={handleDownload}
      className="inline-flex items-center gap-1"
    >
      <Download className="h-4 w-4" />
      Baixar
    </Button>
  );
}

export default function ContratacaoHistorico({ contratacaoId, userContratado }: ContratacaoHistoricoProps) {
  const { toast } = useToast();
  const [historico, setHistorico] = useState<HistoricoEtapa[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewData, setViewData] = useState<ViewData>({});
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [tentativasDocumentos, setTentativasDocumentos] = useState(0);
  const [showExameModal, setShowExameModal] = useState(false);
  const { hasPermission } = usePermissions();

  // Função auxiliar para verificar se está assinado
  const isAssinado = (value: any): boolean => {
    return value === 1 || value === true || value === '1' || value === 'true';
  };

  // Função auxiliar para verificar se está pendente
  const isPendente = (value: any): boolean => {
    return value === 'pendente' || value === 0 || value === false || value === '0' || value === 'false';
  };

  const etapasFluxo = [
    { key: 'proposta_status', nome: 'Proposta', tipo: 'status' },
    { key: 'dados_pessoais', nome: 'Dados Pessoais', tipo: 'data' },
    { key: 'documentos', nome: 'Documentos', tipo: 'data' },
    { key: 'validacao_status', nome: 'Validação', tipo: 'status' },
    { key: 'exame_status', nome: 'Exame', tipo: 'status' },
    { key: 'contrato_assinado', nome: 'Assinatura de Contrato', tipo: 'boolean' },
    { key: 'integracao_concluida', nome: 'Integração Concluída', tipo: 'boolean' }
  ];

  useEffect(() => {
    fetchHistorico();
  }, [contratacaoId, userContratado]);

  const fetchHistorico = async () => {
    try {
      setLoading(true);
      
      // Primeiro, buscar o user_id associado à contratação
      let userId = userContratado;
      
      // Se userContratado é um email, buscar o user_id correspondente
      if (userContratado && userContratado.includes('@')) {
        console.log('Buscando user_id para email:', userContratado);
        
        // Tentar buscar no profiles primeiro
        const { data: profileData } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('email', userContratado)
          .maybeSingle();
        
        if (profileData?.user_id) {
          userId = profileData.user_id;
          console.log('User ID encontrado no profiles:', userId);
        }
      }
      
      // Se não foi passado userContratado ou não encontrou o user_id, buscar pelo contratacaoId
      if (!userId) {
        console.log('Buscando user_id pela contratação:', contratacaoId);
        
        const { data: contratacaoData } = await supabase
          .from('contratacao')
          .select('id_usuario, user_criado, email')
          .eq('id', contratacaoId)
          .maybeSingle();

        if (contratacaoData) {
          userId = contratacaoData.id_usuario || contratacaoData.user_criado;
          console.log('User ID da contratação:', userId);
          
          // Se ainda não encontrou, tentar buscar pelo email da contratação
          if (!userId && contratacaoData.email) {
            const { data: profileData } = await supabase
              .from('profiles')
              .select('user_id')
              .eq('email', contratacaoData.email)
              .maybeSingle();
            
            userId = profileData?.user_id;
            console.log('User ID encontrado pelo email da contratação:', userId);
          }
        }
      }

      // Buscar dados reais do banco para validação
      let dadosPessoaisReais = null;
      let documentosReais = null;
      let contratosReais = null;
      
      if (userId) {
        // Buscar dados pessoais reais
        const { data: dadosData } = await supabase
          .from('dados_pessoais')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();
        dadosPessoaisReais = dadosData;

        // Buscar documentos reais da nova tabela
        const { data: docsData } = await supabase
          .from('documentos_contratacao')
          .select('*')
          .eq('user_id', userId);
        documentosReais = docsData;

        // Buscar contratos reais da tabela contratacao_contratos
        console.log('🔍 Buscando contratos/propostas para contratação:', contratacaoId);
        console.log('🔍 User ID atual:', userId);
        console.log('🔍 Usuário logado (auth.uid()):', currentUserId);
        
        // Primeira tentativa: busca direta por contratacao_id
        let { data: contratosData, error: contratosError } = await supabase
          .from('contratacao_contratos')
          .select('*')
          .eq('contratacao_id', contratacaoId);
          
        console.log('📊 Resultado da primeira busca:', {
          dados: contratosData,
          erro: contratosError,
          quantidade: contratosData?.length || 0
        });
          
        // Se não encontrou, pode ser problema de RLS. Vamos tentar uma consulta mais ampla
        if (!contratosData || contratosData.length === 0) {
          console.log('⚠️ Primeira busca não retornou resultados, tentando busca ampliada...');
          const { data: todosContratos, error: erroTodos } = await supabase
            .from('contratacao_contratos')
            .select('*');
            
          if (erroTodos) {
            console.error('❌ Erro na busca ampliada:', erroTodos);
          } else {
            // Filtrar manualmente no cliente
            contratosData = todosContratos?.filter(c => c.contratacao_id === contratacaoId) || [];
            console.log('📊 Contratos encontrados via busca ampliada:', contratosData.length);
          }
        }
        
        console.log('📄 Contratos/propostas encontrados:', contratosData?.length || 0);
        
        if (contratosError) {
          console.error('❌ Erro ao buscar contratos:', contratosError);
          
          // Tentar busca sem filtro para verificar permissões
          console.log('🔍 Tentando busca sem filtro para verificar permissões...');
          const { data: allContratos, error: allError } = await supabase
            .from('contratacao_contratos')
            .select('*')
            .limit(5);
            
          if (allError) {
            console.error('❌ Erro mesmo sem filtro:', allError);
          } else {
            console.log('✅ Busca sem filtro funcionou, encontrados:', allContratos?.length || 0);
            // Verificar se algum tem nossa contratacao_id
            const nossoContrato = allContratos?.find(c => c.contratacao_id === contratacaoId);
            if (nossoContrato) {
              console.log('✅ Encontrado nosso contrato em busca geral:', {
                id: nossoContrato.id,
                documento: nossoContrato.documento_cpf_nome,
                clicksign: nossoContrato.clicksign_assinado
              });
            }
          }
        } else {
          console.log('📄 Dados dos contratos:', contratosData?.map(c => ({
            id: c.id,
            documento_nome: c.documento_cpf_nome,
            clicksign_assinado: c.clicksign_assinado,
            tipo: c.documento_cpf_nome?.startsWith('Proposta_') ? 'PROPOSTA' : 
                 c.documento_cpf_nome?.startsWith('Contrato_') ? 'CONTRATO' : 'OUTRO'
          })));
        }
        
        contratosReais = contratosData;
      }

      // Buscar dados da evolução
      let evolucaoData = null;
      let contratacaoData = null;
      let tentativasDocumentos = 0;
      
      
      if (userId) {
        console.log('=== BUSCANDO EVOLUÇÃO ===');
        console.log('User ID:', userId);
        console.log('Contratação ID:', contratacaoId);
        
        const { data, error } = await supabase
          .from('evolucao_contratacao')
          .select('*')
          .eq('user_id', userId)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        if (error) {
          console.error('❌ Erro ao buscar evolução:', error);
        } else {
          evolucaoData = data;
        }
        
        console.log('=== DADOS DE EVOLUÇÃO ENCONTRADOS ===');
        console.log('Raw data:', data);
        console.log('Dados completos:', JSON.stringify(evolucaoData, null, 2));
        
        if (!evolucaoData) {
          console.log('❌ Nenhum dado de evolução encontrado para o usuário:', userId);
        } else {
          console.log('✅ Dados de evolução encontrados!');
          console.log('Etapa atual:', evolucaoData.etapa_atual);
          console.log('Dados pessoais raw:', evolucaoData.dados_pessoais);
          console.log('Documentos raw:', evolucaoData.documentos);
          console.log('Validação status:', evolucaoData.validacao_status);
          console.log('Exame status:', evolucaoData.exame_status);
        }
      }
      
      // Buscar dados da contratação para complementar informações de aceite e tentativas
      const { data: contratacaoResult } = await supabase
        .from('contratacao')
        .select('bt_aceite, dt_aceite, stage, tentativas_documentos, data_exame')
        .eq('id', contratacaoId)
        .maybeSingle();
      
      contratacaoData = contratacaoResult;
      const tentativas = contratacaoData?.tentativas_documentos || 0;
      setTentativasDocumentos(tentativas);

      // Processar todas as etapas (concluídas e pendentes) com validação real dos dados
      const historicoProcessado: HistoricoEtapa[] = [];

      etapasFluxo.forEach((etapa, index) => {
        let status = 'pendente';
        let dataHora = '';

        console.log(`\n=== PROCESSANDO ETAPA: ${etapa.nome} (${etapa.key}) ===`);

        // Lógica para a etapa de proposta usando a nova estrutura contratacao_contratos
        if (etapa.key === 'proposta_status') {
          console.log('🔍 Analisando proposta...');
          console.log('📊 Contratos disponíveis:', contratosReais?.length || 0);
          console.log('🆔 ContratacaoId atual:', contratacaoId);
          
          // Debug: Mostrar todos os contratos disponíveis
          if (contratosReais && contratosReais.length > 0) {
            console.log('📋 Lista completa de contratos:');
            contratosReais.forEach((c, index) => {
              console.log(`  [${index}] ID: ${c.id}`);
              console.log(`  [${index}] ContratacaoID: ${c.contratacao_id}`);
              console.log(`  [${index}] Documento: ${c.documento_cpf_nome}`);
              console.log(`  [${index}] ClickSign: ${c.clicksign_assinado} (tipo: ${typeof c.clicksign_assinado})`);
              console.log(`  [${index}] Match Contratacao: ${c.contratacao_id === contratacaoId}`);
              console.log(`  [${index}] Tem 'proposta' no nome: ${c.documento_cpf_nome && c.documento_cpf_nome.toLowerCase().includes('proposta')}`);
              console.log('  ---');
            });
          }
          
          // Buscar proposta especificamente na tabela contratacao_contratos
          console.log('🔍 Buscando proposta específica...');
          console.log('📋 Contratos disponíveis para análise:', contratosReais?.length || 0);
          
          let proposta = contratosReais?.find(c => {
            const isContratacao = c.contratacao_id === contratacaoId;
            const docName = c.documento_cpf_nome || '';
            const hasPropostaDoc = docName.toLowerCase().includes('proposta');
            
            console.log(`✅ Verificando contrato ${c.id}:`, {
              contratacao_id: c.contratacao_id,
              contratacao_match: isContratacao,
              documento_nome: docName,
              has_proposta_keyword: hasPropostaDoc,
              clicksign_status: c.clicksign_assinado,
              clicksign_type: typeof c.clicksign_assinado,
              org_id: c.org_id
            });
            
            return isContratacao && hasPropostaDoc;
          });
          
          // Se não encontrou, tentar busca alternativa por string mais flexível
          if (!proposta) {
            console.log('⚠️ Primeira busca falhou, tentando busca alternativa...');
            proposta = contratosReais?.find(c => {
              const isContratacao = c.contratacao_id === contratacaoId;
              const docName = c.documento_cpf_nome?.toLowerCase() || '';
              // Buscar variações: Proposta_, proposta_, PROPOSTA, etc.
              const hasPropostaVariant = docName.includes('proposta') || 
                                        docName.startsWith('proposta_') ||
                                        docName.includes('Proposta_');
              console.log(`✅ Verificando contrato ${c.id} (estratégia 2):`, {
                contratacao_match: isContratacao,
                documento_nome: c.documento_cpf_nome,
                has_proposta_variant: hasPropostaVariant,
                clicksign_status: c.clicksign_assinado
              });
              return isContratacao && hasPropostaVariant;
            });
          }
          
          // Se ainda não encontrou, tentar buscar qualquer contrato da contratação
          if (!proposta) {
            console.log('⚠️ Segunda busca falhou, tentando buscar qualquer documento da contratação...');
            proposta = contratosReais?.find(c => c.contratacao_id === contratacaoId);
            if (proposta) {
              console.log('✅ Encontrado documento genérico:', {
                id: proposta.id,
                documento_nome: proposta.documento_cpf_nome,
                clicksign_status: proposta.clicksign_assinado
              });
            }
          }
          
          console.log('📄 Proposta encontrada:', proposta ? {
            id: proposta.id,
            clicksign_assinado: proposta.clicksign_assinado,
            documento_nome: proposta.documento_cpf_nome,
            tipo_valor: typeof proposta.clicksign_assinado
          } : 'Nenhuma proposta encontrada');
          
          if (proposta) {
            // Verificar diferentes formatos do campo clicksign_assinado
            const assinado = proposta.clicksign_assinado;
            console.log('🔍 Verificando status de assinatura:', {
              valor: assinado,
              tipo: typeof assinado,
              string_1: assinado === '1',
              number_1: assinado === 1,
              boolean_true: assinado === true
            });
            
            if (assinado === '1' || assinado === 1 || assinado === true || assinado === 'true') {
              status = 'concluido';
              dataHora = proposta.updated_at || proposta.created_at;
              console.log('✅ Proposta ASSINADA via ClickSign');
            } else if (assinado === '0' || assinado === 0 || assinado === false || assinado === 'false' || assinado === 'pendente') {
              status = 'em_andamento';
              dataHora = proposta.created_at;
              console.log('⏳ Proposta PENDENTE via ClickSign');
            } else {
              console.log('⚠️ Status de assinatura não reconhecido:', assinado);
              status = 'em_andamento';
              dataHora = proposta.created_at;
            }
          } else {
            console.log('❌ Nenhuma proposta encontrada na tabela contratacao_contratos');
          }
          
          // Verificar também na tabela contratacao se a proposta foi aceita (fallback)
          if ((status === 'pendente' || status === 'em_andamento') && contratacaoData) {
            if (contratacaoData.bt_aceite === 1 || contratacaoData.aceite_proposta === 1 || contratacaoData.proposta_assinada_em) {
              status = 'concluido';
              dataHora = contratacaoData.proposta_assinada_em || contratacaoData.dt_aceite || contratacaoData.updated_at;
              console.log('✅ Proposta aceita via tabela contratacao (fallback)');
            }
          }
          
          console.log(`🎯 Proposta status final: ${status}`);
        }
        
        // Validação especial para dados pessoais
        else if (etapa.key === 'dados_pessoais') {
          console.log('🔍 Analisando dados pessoais...');
          console.log('Dados pessoais reais:', !!dadosPessoaisReais);
          console.log('Evolução dados_pessoais (raw):', evolucaoData?.dados_pessoais);
          console.log('Tipo de dados_pessoais:', typeof evolucaoData?.dados_pessoais);
          
          // Priorizar dados da evolução que são mais confiáveis
          if (evolucaoData?.dados_pessoais && typeof evolucaoData.dados_pessoais === 'object') {
            console.log('Verificando dados_pessoais da evolução...');
            const dadosEvolucao = evolucaoData.dados_pessoais as any;
            if (dadosEvolucao?.status === 'concluido') {
              status = 'concluido';
              dataHora = dadosEvolucao?.timestamp || evolucaoData.updated_at;
              console.log('✅ Dados pessoais concluídos (evolução)');
            } else if (evolucaoData?.etapa_atual === 'dados_pessoais') {
              status = 'em_andamento';
              dataHora = evolucaoData.updated_at;
              console.log('🔄 Dados pessoais em andamento');
            }
          }
          // Fallback para dados reais apenas se não há dados na evolução
          else if (dadosPessoaisReais && dadosPessoaisReais.nome_completo && dadosPessoaisReais.cpf) {
            status = 'concluido';
            dataHora = dadosPessoaisReais.updated_at || dadosPessoaisReais.created_at;
            console.log('✅ Dados pessoais concluídos (dados reais)');
          }
          console.log(`Dados pessoais status final: ${status}`);
        }
        
        // Validação especial para documentos
        else if (etapa.key === 'documentos') {
          console.log('🔍 Analisando documentos...');
          console.log('Documentos reais:', documentosReais?.length || 0);
          console.log('Evolução documentos (raw):', evolucaoData?.documentos);
          console.log('Tipo de documentos:', typeof evolucaoData?.documentos);
          
          // Priorizar dados da evolução que são mais confiáveis
          if (evolucaoData?.documentos && typeof evolucaoData.documentos === 'object') {
            console.log('Verificando documentos da evolução...');
            const docsEvolucao = evolucaoData.documentos as any;
            if (docsEvolucao?.status === 'concluido') {
              status = 'concluido';
              dataHora = docsEvolucao?.timestamp || evolucaoData.updated_at;
              console.log('✅ Documentos concluídos (evolução)');
            } else if (evolucaoData?.etapa_atual === 'documentos') {
              status = 'em_andamento';
              dataHora = evolucaoData.updated_at;
              console.log('🔄 Documentos em andamento');
            }
          }
          // Fallback para dados reais apenas se não há dados na evolução
          else if (documentosReais && documentosReais.length > 0) {
            status = 'concluido';
            dataHora = documentosReais[0].updated_at || documentosReais[0].created_at;
            console.log('✅ Documentos concluídos (dados reais)');
          }
          console.log(`Documentos status final: ${status}`);
        }
        
        // Lógica para contratos assinados usando a nova estrutura contratacao_contratos
        else if (etapa.key === 'contrato_assinado') {
          console.log('🔍 Analisando contratos...');
          
          // Buscar contratos na tabela contratacao_contratos
          const contratos = contratosReais?.filter(c => 
            c.contratacao_id === contratacaoId && 
            c.documento_cpf_nome && 
            c.documento_cpf_nome.startsWith('Contrato_')
          );
          
          console.log('Contratos encontrados:', contratos?.length || 0, contratos?.map(c => ({
            id: c.id,
            clicksign_assinado: c.clicksign_assinado,
            documento_nome: c.documento_cpf_nome
          })));
          
          if (contratos && contratos.length > 0) {
            // Verificar se todos os contratos estão assinados (diferentes formatos possíveis)
            const todosAssinados = contratos.every(c => 
              c.clicksign_assinado === '1' || 
              c.clicksign_assinado === 1 || 
              c.clicksign_assinado === true
            );
            
            const algumPendente = contratos.some(c => 
              c.clicksign_assinado === '0' || 
              c.clicksign_assinado === 0 || 
              c.clicksign_assinado === 'pendente' || 
              c.clicksign_assinado === false
            );
            
            if (todosAssinados) {
              status = 'concluido';
              // Data do último contrato assinado
              const ultimoAssinado = contratos
                .filter(c => c.clicksign_assinado === '1' || c.clicksign_assinado === 1 || c.clicksign_assinado === true)
                .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())[0];
              dataHora = ultimoAssinado?.updated_at || '';
              console.log('✅ Todos os contratos assinados via ClickSign');
            } else if (algumPendente) {
              status = 'em_andamento';
              dataHora = contratos[0]?.created_at || '';
              console.log('⏳ Contratos pendentes via ClickSign');
            }
          }
          
          console.log(`Contratos status final: ${status}`);
        }
        
        // Para etapa de exame, verificar se há data_exame na tabela contratacao
        else if (etapa.key === 'exame_status') {
          // Primeiro verificar na evolução se existir
          if (evolucaoData) {
            const valorCampo = evolucaoData[etapa.key as keyof typeof evolucaoData];
            console.log(`Exame status na evolução:`, valorCampo, `Etapa atual:`, evolucaoData.etapa_atual);
            
            if (valorCampo === 'concluido') {
              status = 'concluido';
              dataHora = evolucaoData.updated_at;
            } else if (valorCampo === 'em_andamento') {
              status = 'em_andamento';
              dataHora = evolucaoData.updated_at;
            } else if (evolucaoData.etapa_atual === 'exame_admissional' || evolucaoData.etapa_atual === 'exame') {
              // Se está na etapa de exame admissional, mostrar como em andamento mesmo se pendente
              status = 'em_andamento';
              dataHora = evolucaoData.updated_at;
            }
          }
          
          // Se não há dados de evolução ou está pendente, verificar se há data_exame na contratação
          if (status === 'pendente' && contratacaoData?.data_exame) {
            status = 'em_andamento';
            dataHora = contratacaoData.data_exame;
          }
        }
        
        // Para outras etapas, usar a lógica original com validações
        else if (evolucaoData) {
          const valorCampo = evolucaoData[etapa.key as keyof typeof evolucaoData];
          
          console.log(`Analisando etapa ${etapa.nome} (${etapa.key}):`, valorCampo, `Etapa atual:`, evolucaoData.etapa_atual);

          if (etapa.tipo === 'boolean') {
            // Campos boolean que podem vir como string 'true'/'false' ou boolean true/false
            if (valorCampo === true || valorCampo === 'true') {
              status = 'concluido';
              dataHora = evolucaoData.updated_at;
            }
          } else if (etapa.tipo === 'status') {
            // Campos de status (validacao_status, exame_status)
            if (valorCampo === 'concluido') {
              status = 'concluido';
              dataHora = evolucaoData.updated_at;
            } else if (valorCampo === 'em_andamento') {
              status = 'em_andamento';
              dataHora = evolucaoData.updated_at;
            } else if (etapa.key === 'validacao_status' && valorCampo === 'concluido') {
              // Para validação, verificar explicitamente se está concluído
              status = 'concluido';
              dataHora = evolucaoData.updated_at;
            }
          }
        }

        // Lógica para marcar etapas anteriores como concluídas se o usuário está numa etapa posterior
        if (evolucaoData?.etapa_atual) {
          const etapaAtualIndex = etapasFluxo.findIndex(e => e.key === evolucaoData.etapa_atual || 
            (evolucaoData.etapa_atual === 'dados_pessoais' && e.key === 'dados_pessoais') ||
            (evolucaoData.etapa_atual === 'documentos' && e.key === 'documentos') ||
            (evolucaoData.etapa_atual === 'validacao' && e.key === 'validacao_status') ||
            (evolucaoData.etapa_atual === 'exame' && e.key === 'exame_status') ||
            (evolucaoData.etapa_atual === 'exame_admissional' && e.key === 'exame_status') ||
            (evolucaoData.etapa_atual === 'contrato' && e.key === 'contrato_assinado') ||
            (evolucaoData.etapa_atual === 'integracao' && e.key === 'integracao_concluida')
          );
          
          // Se o usuário está numa etapa posterior e a etapa anterior não está marcada como concluída,
          // verificar se existem dados reais para marcá-la como concluída
          if (etapaAtualIndex > index) {
            if (etapa.key === 'proposta_status' && (contratacaoData?.bt_aceite === 1 || evolucaoData?.proposta_status === 'concluido')) {
              status = 'concluido';
              dataHora = contratacaoData?.dt_aceite || evolucaoData.updated_at;
            } else if (etapa.key === 'dados_pessoais') {
              // Se está numa etapa posterior à dados pessoais, marcar como concluído baseado nos dados
              if (dadosPessoaisReais?.nome_completo || 
                  (evolucaoData?.dados_pessoais && typeof evolucaoData.dados_pessoais === 'object' && 
                   (evolucaoData.dados_pessoais as any)?.status === 'concluido')) {
                status = 'concluido';
                dataHora = dadosPessoaisReais?.updated_at || 
                          (typeof evolucaoData?.dados_pessoais === 'object' ? 
                           (evolucaoData.dados_pessoais as any)?.timestamp : null) || 
                          evolucaoData?.updated_at;
              }
            } else if (etapa.key === 'documentos') {
              // Se está numa etapa posterior aos documentos, marcar como concluído baseado nos dados
              if (documentosReais?.length > 0 || 
                  (evolucaoData?.documentos && typeof evolucaoData.documentos === 'object' && 
                   (evolucaoData.documentos as any)?.status === 'concluido')) {
                status = 'concluido';
                dataHora = documentosReais?.[0]?.updated_at || 
                          (typeof evolucaoData?.documentos === 'object' ? 
                           (evolucaoData.documentos as any)?.timestamp : null) || 
                          evolucaoData?.updated_at;
              }
            } else if (etapa.key === 'validacao_status') {
              // Se está numa etapa posterior à validação, marcar como concluído
              if (evolucaoData?.validacao_status === 'concluido' || 
                  evolucaoData?.etapa_atual === 'integracao' ||
                  (evolucaoData?.etapa_atual === 'exame_admissional' && evolucaoData?.validacao_status === 'concluido')) {
                status = 'concluido';
                dataHora = evolucaoData.updated_at;
              }
            } else if (etapa.key === 'exame_status' && evolucaoData.etapa_atual === 'integracao') {
              // Se está na integração, marcar exame como concluído
              status = 'concluido';
              dataHora = evolucaoData.updated_at;
            } else if (etapa.key === 'contrato_assinado' && evolucaoData.etapa_atual === 'integracao') {
              // Se está na integração, marcar contrato como concluído
              status = 'concluido';
              dataHora = evolucaoData.updated_at;
            }
          }
        }
        
        // Fallback: Se não há dados de evolução, usar dados básicos da contratação
        if (!evolucaoData && status === 'pendente') {
          // Se a contratação foi criada, pelo menos mostrar a primeira etapa
          if (etapa.key === 'proposta_status' && contratacaoData) {
            status = 'em_andamento';
            dataHora = contratacaoData.created_at || '';
          }
        }

        // Adicionar todas as etapas ao histórico
        historicoProcessado.push({
          etapa: etapa.nome,
          status,
          dataHora: dataHora || (evolucaoData?.created_at || '')
        });
      });

      setHistorico(historicoProcessado);
      setCurrentUserId(userId);
      console.log('setCurrentUserId chamado com:', userId);

    } catch (error: any) {
      toast({
        title: "Erro ao carregar histórico",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'concluido':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'em_andamento':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      default:
        return <Circle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'concluido':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Concluído</Badge>;
      case 'em_andamento':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Em Andamento</Badge>;
      default:
        return <Badge variant="outline" className="text-muted-foreground border-muted">Pendente</Badge>;
    }
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const loadViewData = async (tipo: string) => {
    if (!currentUserId) return;

    try {
      switch (tipo) {
        case 'proposta':
          const { data: contratacaoData } = await supabase
            .from('contratacao')
            .select('proposta_html, html_proposta')
            .eq('id', contratacaoId)
            .single();
          
          setViewData(prev => ({ ...prev, proposta: contratacaoData }));
          break;

        case 'dados_pessoais':
          const { data: dadosData } = await supabase
            .from('dados_pessoais')
            .select('*')
            .eq('user_id', currentUserId)
            .single();
          
          setViewData(prev => ({ ...prev, dadosPessoais: dadosData }));
          break;

        case 'documentos':
          const { data: docsData } = await supabase
            .from('documentos_contratacao')
            .select('*')
            .eq('user_id', currentUserId)
            .order('created_at', { ascending: false });
          
          setViewData(prev => ({ ...prev, documentos: docsData }));
          break;

        case 'exame':
          // Buscar informações de exame da contratação
          const { data: exameInfo } = await supabase
            .from('contratacao')
            .select('data_exame, horario_exame, endereco_exame, guia_exame_path')
            .eq('id', contratacaoId)
            .single();

          let atestadoData = null;
          
          console.log('Buscando atestado para currentUserId:', currentUserId);
          
          // Para o usuário específico, usar CPF hardcoded diretamente
          let cpfUsuario = null;
          if (currentUserId === 'davisilva.x13@gmail.com') {
            cpfUsuario = '54283387843';
            console.log('Usando CPF hardcoded para davisilva.x13@gmail.com:', cpfUsuario);
          } else if (currentUserId && !currentUserId.includes('@')) {
            // Se currentUserId é um UUID válido, tentar buscar CPF
            try {
              const { data: dadosPessoaisCpf } = await supabase
                .from('dados_pessoais')
                .select('cpf')
                .eq('user_id', currentUserId)
                .maybeSingle();
              
              cpfUsuario = dadosPessoaisCpf?.cpf;
              console.log('CPF encontrado pelos dados pessoais:', cpfUsuario);
            } catch (error) {
              console.log('Erro ao buscar CPF pelos dados pessoais:', error);
            }
          }
          
          if (cpfUsuario) {
            console.log('Buscando atestado para CPF:', cpfUsuario);
            
            // Buscar arquivos no storage que contenham "Atestado de Saude" na pasta do CPF
            const { data: storageFiles } = await supabase.storage
              .from('contratacao-documentos')
              .list(cpfUsuario, { limit: 100 });
            
            console.log('Arquivos encontrados no storage:', storageFiles);
            
            const atestadoFile = storageFiles?.find(file => 
              file.name.toLowerCase().includes('atestado') && 
              file.name.toLowerCase().includes('saude')
            );
            
            console.log('Arquivo de atestado encontrado:', atestadoFile);
            
            if (atestadoFile) {
              // Criar um objeto simulando a estrutura esperada
              atestadoData = {
                id: atestadoFile.id,
                nome_arquivo: atestadoFile.name,
                tipo_documento: 'atestado_saude',
                caminho_storage: `${cpfUsuario}/${atestadoFile.name}`,
                status_validacao: 'pendente',
                user_id: currentUserId,
                created_at: atestadoFile.created_at,
                observacoes: 'Atestado encontrado no storage'
              };
              console.log('Dados do atestado criados:', atestadoData);
            }
          } else {
            console.log('CPF não encontrado para buscar atestado');
          }
          
          console.log('Dados do atestado para exibição:', atestadoData);
          
          setViewData(prev => ({ 
            ...prev, 
            atestadoSaude: atestadoData,
            exameInfo: exameInfo
          }));
          break;

        case 'contratos':
          const { data: contratosData } = await supabase
            .from('contratacao_contratos')
            .select(`
              *,
              tbl_modelo_contrato (
                nome_modelo
              )
            `)
            .eq('user_contratado', currentUserId)
            .order('created_at', { ascending: false });
          
          setViewData(prev => ({ ...prev, contratos: contratosData }));
          break;
      }
    } catch (error: any) {
      toast({
        title: "Erro ao carregar dados",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getViewButton = (etapa: string, status: string) => {
    // Permitir visualização se a etapa estiver concluída OU em andamento
    if (status === 'pendente') return null;

    const buttonConfigs = {
      'Proposta': { 
        icon: <FileText className="h-4 w-4" />, 
        onClick: () => loadViewData('proposta'),
        key: 'proposta'
      },
      'Dados Pessoais': { 
        icon: <User className="h-4 w-4" />, 
        onClick: () => loadViewData('dados_pessoais'),
        key: 'dados_pessoais'
      },
      'Documentos': { 
        icon: <ClipboardList className="h-4 w-4" />, 
        onClick: () => loadViewData('documentos'),
        key: 'documentos'
      },
      'Exame': { 
        icon: <Heart className="h-4 w-4" />, 
        onClick: () => setShowExameModal(true),
        key: 'exame',
        useModal: false // Indica que este botão não usa o modal padrão
      },
      'Contrato Assinado': { 
        icon: <ScrollText className="h-4 w-4" />, 
        onClick: () => loadViewData('contratos'),
        key: 'contratos'
      }
    };

    const config = buttonConfigs[etapa as keyof typeof buttonConfigs];
    if (!config) return null;

    // Se for exame e tem permissão de RH, mostrar opções específicas
    if (etapa === 'Exame' && hasPermission('recursos_humanos', 'contratacao', 'write')) {
      return (
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowExameModal(true)}
          >
            <Eye className="h-4 w-4 mr-1" />
            Ver Atestado
          </Button>
          {status === 'em_andamento' && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowExameModal(true)}
            >
              <Heart className="h-4 w-4 mr-1" />
              Aprovar/Reprovar
            </Button>
          )}
        </div>
      );
    }

    // Se for exame mas não tem permissão de RH, apenas mostrar visualização
    if (etapa === 'Exame') {
      return (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setShowExameModal(true)}
        >
          <Eye className="h-4 w-4 mr-1" />
          Ver
        </Button>
      );
    }
    
    // Para outras etapas concluídas
    if (status === 'concluido') {
      return (
        <Dialog>
          <DialogTrigger asChild>
            <Button 
              variant="outline" 
              size="sm" 
              className="ml-2"
              onClick={config.onClick}
            >
              <Eye className="h-4 w-4 mr-1" />
              Ver
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {config.icon}
                {etapa}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {renderModalContent(config.key)}
            </div>
          </DialogContent>
        </Dialog>
      );
    }
  };

  const renderModalContent = (tipo: string) => {
    switch (tipo) {
      case 'proposta':
        const proposta = viewData.proposta;
        if (!proposta) return <div>Carregando proposta...</div>;
        
        return (
          <div className="space-y-4">
            <div 
              className="prose max-w-none border rounded-lg p-4 bg-muted/30"
              dangerouslySetInnerHTML={{ 
                __html: proposta.proposta_html || proposta.html_proposta || 'Proposta não encontrada'
              }}
            />
          </div>
        );

      case 'dados_pessoais':
        const dados = viewData.dadosPessoais;
        if (!dados) return <div>Carregando dados pessoais...</div>;
        
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><strong>Nome Completo:</strong> {dados.nome_completo}</div>
            <div><strong>CPF:</strong> {dados.cpf}</div>
            <div><strong>RG:</strong> {dados.rg}</div>
            <div><strong>Data de Nascimento:</strong> {dados.data_nascimento}</div>
            <div><strong>Gênero:</strong> {dados.genero}</div>
            <div><strong>Estado Civil:</strong> {dados.estado_civil}</div>
            <div><strong>Nacionalidade:</strong> {dados.nacionalidade}</div>
            <div><strong>Cor da Pele:</strong> {dados.cor_pele}</div>
            <div><strong>Celular:</strong> {dados.celular}</div>
            <div><strong>CEP:</strong> {dados.cep}</div>
            <div><strong>Logradouro:</strong> {dados.logradouro}</div>
            <div><strong>Número:</strong> {dados.numero}</div>
            <div><strong>Complemento:</strong> {dados.complemento}</div>
            <div><strong>Bairro:</strong> {dados.bairro}</div>
            <div><strong>Cidade:</strong> {dados.cidade}</div>
            <div><strong>Estado:</strong> {dados.estado}</div>
            <div><strong>Banco:</strong> {dados.banco}</div>
            <div><strong>Agência:</strong> {dados.agencia}</div>
            <div><strong>Conta:</strong> {dados.conta}</div>
            <div><strong>Tipo de Conta:</strong> {dados.tipo_conta}</div>
            <div><strong>Chave PIX:</strong> {dados.chave_pix}</div>
            <div><strong>Carteira de Trabalho:</strong> {dados.carteira_trabalho}</div>
            <div><strong>Título de Eleitor:</strong> {dados.titulo_eleitor}</div>
            <div><strong>Número PIS:</strong> {dados.numero_pis}</div>
          </div>
        );

      case 'documentos':
        return (
          <div className="space-y-4">
            {hasPermission('recursos_humanos', 'contratacao', 'read') && currentUserId ? (
              <DocumentosValidacaoRH 
                contratacaoId={contratacaoId} 
                userId={currentUserId} 
              />
            ) : !hasPermission('recursos_humanos', 'contratacao', 'read') ? (
              (() => {
                const documentos = viewData.documentos;
                if (!documentos) return <div>Carregando documentos...</div>;
                
                // Filtrar documentos para excluir atestado de saúde (exibido apenas na seção Exame)
                const documentosFiltrados = documentos.filter(doc => doc.tipo_documento !== 'atestado_saude');
                
                return <DocumentListView documentos={documentosFiltrados} />;
              })()
            ) : (
              <div>Carregando informações do usuário...</div>
            )}
          </div>
        );

      case 'exame':
        const atestado = viewData.atestadoSaude;
        const exameInfo = viewData.exameInfo;
        
        return (
          <div className="space-y-4">
            {/* Informações do Exame */}
            {exameInfo && (
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-2">Informações do Exame</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {exameInfo.data_exame && (
                    <div><strong>Data do Exame:</strong> {new Date(exameInfo.data_exame).toLocaleDateString('pt-BR')}</div>
                  )}
                  {exameInfo.horario_exame && (
                    <div><strong>Horário:</strong> {exameInfo.horario_exame}</div>
                  )}
                  {exameInfo.endereco_exame && (
                    <div className="md:col-span-2"><strong>Local:</strong> {exameInfo.endereco_exame}</div>
                  )}
                  {exameInfo.guia_exame_path && (
                    <div className="md:col-span-2">
                      <strong>Guia de Exame:</strong>
                      <div className="mt-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => window.open(exameInfo.guia_exame_path, '_blank')}
                          className="inline-flex items-center gap-1"
                        >
                          <Download className="h-4 w-4" />
                          Baixar Guia
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Atestado de Saúde */}
            {atestado ? (
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-2">Atestado de Saúde</h4>
                <div className="space-y-2">
                  <p><strong>Nome do Arquivo:</strong> {atestado.nome_arquivo}</p>
                  <p><strong>Status:</strong> {atestado.status_validacao}</p>
                  {atestado.observacoes && (
                    <p><strong>Observações:</strong> {atestado.observacoes}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Enviado em: {new Date(atestado.created_at).toLocaleString('pt-BR')}
                  </p>
                  
                  <div className="mt-2">
                    <DocumentDownloadButton documento={atestado} />
                  </div>
                </div>
                
                {/* Renderizar o atestado */}
                <div className="mt-4">
                  <DocumentViewer documento={atestado} />
                </div>
              </div>
            ) : (
              <div className="border rounded-lg p-4 text-center text-muted-foreground">
                Atestado de saúde ainda não enviado
              </div>
            )}
          </div>
        );

      case 'contratos':
        const contratos = viewData.contratos;
        if (!contratos) return <div>Carregando contratos...</div>;
        
        return (
          <div className="space-y-4">
            {contratos.length === 0 ? (
              <div>Nenhum contrato encontrado</div>
            ) : (
              contratos.map((contrato, index) => (
                <div key={contrato.id} className="border rounded-lg p-4">
                  {index > 0 && <hr className="mb-4" />}
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium">
                        {contrato.tbl_modelo_contrato?.nome_modelo || 'Contrato'}
                      </h4>
                      <Badge variant={contrato.bt_aceite ? 'default' : 'secondary'}>
                        {contrato.bt_aceite ? 'Assinado' : 'Pendente'}
                      </Badge>
                    </div>
                    
                    {contrato.bt_aceite && contrato.dt_aceite && (
                      <p className="text-sm text-muted-foreground">
                        Assinado em: {new Date(contrato.dt_aceite).toLocaleString('pt-BR')}
                      </p>
                    )}
                    
                    <div 
                      className="prose max-w-none border rounded p-3 bg-muted/30 text-sm"
                      dangerouslySetInnerHTML={{ __html: contrato.html_contrato }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        );

      default:
        return <div>Tipo de conteúdo não reconhecido</div>;
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8">Carregando histórico...</div>;
  }

  return (
    <div>
      {historico.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Circle className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Nenhum progresso encontrado</p>
          <p className="text-sm mt-2">Não foi possível localizar dados de evolução para esta contratação</p>
        </div>
      ) : (
        <div className="space-y-0">
          {/* Timeline Vertical Contínua */}
          <div className="relative">
            {historico.map((item, index) => (
              <div key={index} className="relative flex items-start gap-4 pb-8">
                {/* Linha de conexão vertical */}
                {index < historico.length - 1 && (
                  <div 
                    className={`absolute left-2.5 top-8 w-0.5 h-8 ${
                      item.status === 'concluido' ? 'bg-green-400' : 'bg-gray-300'
                    }`}
                  ></div>
                )}
                
                 {/* Ícone da etapa */}
                 <div className="flex-shrink-0 mt-1 relative z-10">
                   <div className="flex items-center gap-1">
                     {getStatusIcon(item.status)}
                     {item.etapa === 'Documentos' && tentativasDocumentos >= 3 && (
                       <AlertTriangle className="h-4 w-4 text-yellow-600" />
                     )}
                   </div>
                 </div>
                
                 {/* Conteúdo da etapa */}
                 <div className="flex-1 min-w-0">
                   <div className="flex items-center justify-between gap-2 mb-2">
                     <div className="flex items-center gap-2">
                       <h4 className={`font-medium text-base ${
                         item.status === 'concluido' ? 'text-foreground' : 'text-muted-foreground'
                       }`}>
                         {item.etapa}
                       </h4>
                       {getViewButton(item.etapa, item.status)}
                     </div>
                     {getStatusBadge(item.status)}
                   </div>
                  {item.dataHora && item.status !== 'pendente' && (
                    <p className="text-sm text-muted-foreground">
                      {formatDateTime(item.dataHora)}
                    </p>
                  )}
                  {item.status === 'pendente' && (
                    <p className="text-sm text-muted-foreground">
                      Aguardando conclusão
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Modal de Aprovação de Exames */}
      {showExameModal && currentUserId && (
        <ExameAprovacaoModal
          isOpen={showExameModal}
          onClose={() => setShowExameModal(false)}
          userId={currentUserId}
          onStatusChange={fetchHistorico}
        />
      )}
    </div>
  );
}